/**
 * SlotPickerModal — popup "dove lo schiero?" partendo dal GIOCATORE.
 *
 * Andrea 2026-09-09: dalla rosa disponibile (tribuna) si poteva solo
 * trascinare, oppure fare il giro inverso (click sullo slot vuoto →
 * scegli il giocatore). Su mobile il drag e' impraticabile: ora un click
 * sul giocatore apre questo popup con tutti i ruoli del modulo (piu' la
 * panchina) e lo si schiera al volo. Il doppio click resta la
 * scorciatoia "nel suo ruolo primario" (gestita in FormazionePage).
 */

import type { RosaPlayer } from "../../hooks/useFase2";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { COMPAT_LABEL, type SlotCompat, slotCompatibility } from "../common/ruoliMantraCompat";
import type { SlotState } from "./Pitch";
import { labelsClassicDaModulo, type Modulo } from "./pitchLayouts";
import { siglaSlot, sigleRuoli } from "../common/nomenclatura";
import { tr } from "../../i18n";

const REPARTO_NOME: Record<string, string> = {
  P: tr("Portiere"),
  D: tr("Difesa"),
  C: tr("Centrocampo"),
  A: tr("Attacco"),
};

/** Stile del bottone-slot in base alla compatibilita' col giocatore. */
const COMPAT_BTN: Record<SlotCompat, string> = {
  exact: "border-success/50 hover:border-success hover:bg-success/10",
  malus: "border-warning/50 hover:border-warning hover:bg-warning/10",
  incompatible: "border-danger/30 opacity-40 cursor-not-allowed",
};

const COMPAT_ICONA: Record<SlotCompat, string> = {
  exact: "✓",
  malus: "⚠",
  incompatible: "✗",
};

interface SlotPickerModalProps {
  player: RosaPlayer;
  slots: SlotState[];
  bench: SlotState[];
  slotLabels: string[];
  modulo: Modulo;
  isMantra: boolean;
  /** Motivo per cui il popup si e' aperto da solo (doppio click a vuoto). */
  avviso?: string | null;
  onSelect: (dest: "slot" | "bench", idx: number) => void;
  onCancel: () => void;
}

export function SlotPickerModal({
  player,
  slots,
  bench,
  slotLabels,
  modulo,
  isMantra,
  avviso,
  onSelect,
  onCancel,
}: SlotPickerModalProps) {
  // Nomenclatura Pro (29/09): sigle tradotte solo se gli slot sono Pro.
  const slotPro = !!isMantra;
  // Raggruppa gli 11 slot per reparto (P / D / C / A) mantenendo l'ordine
  // del modulo: i reparti contigui con la stessa lettera fanno un blocco
  // solo (es. 4-2-3-1 → un unico "Centrocampo" da 5).
  const reparti = labelsClassicDaModulo(modulo);
  const gruppi: { reparto: string; idxs: number[] }[] = [];
  reparti.forEach((r, idx) => {
    const ultimo = gruppi[gruppi.length - 1];
    if (ultimo && ultimo.reparto === r) ultimo.idxs.push(idx);
    else gruppi.push({ reparto: r, idxs: [idx] });
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-primary-soft flex items-center gap-2">
          <PlayerAvatar nome={player.nome} size={36} className="shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-fg truncate">{player.nome}</div>
            <div className="text-[10px] uppercase tracking-wider text-fg-muted font-mono truncate">
              {(player.ruoli_mantra ? sigleRuoli(player.ruoli_mantra) : player.ruolo_classic) ??
                "?"}
              {player.club_serie_a ? tr(" · {{v1}}", { v1: player.club_serie_a }) : ""}
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-fg-muted hover:text-fg text-2xl leading-none shrink-0"
            aria-label={tr("Chiudi")}
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-3 space-y-3">
          {avviso && (
            <div className="text-[11px] text-warning bg-warning/10 border border-warning/30 rounded p-2">
              {avviso}
            </div>
          )}
          <div className="text-[11px] text-fg-muted">
            {tr(
              tr(
                "Scegli il ruolo: lo schieriamo lì. Se lo slot è già occupato, chi c'era torna nella rosa disponibile.",
              ),
            )}
          </div>

          {gruppi.map((g, gi) => (
            <div key={`${g.reparto}-${gi}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-fg-muted mb-1">
                {REPARTO_NOME[g.reparto] ?? g.reparto}
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {g.idxs.map((idx) => {
                  const label = slotLabels[idx] || reparti[idx];
                  const compat = slotCompatibility(player, label, isMantra, modulo);
                  const occupato = slots[idx]?.nome;
                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={compat === "incompatible"}
                      onClick={() => onSelect("slot", idx)}
                      title={COMPAT_LABEL[compat].tag}
                      className={`text-left px-2 py-1.5 rounded border bg-primary-deep/40 transition ${COMPAT_BTN[compat]}`}
                    >
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-bold text-xs text-fg truncate">
                          {siglaSlot(label, slotPro)}
                        </span>
                        <span
                          className={`ml-auto text-[10px] shrink-0 ${COMPAT_LABEL[compat].class}`}
                        >
                          {COMPAT_ICONA[compat]}
                        </span>
                      </div>
                      <div
                        className={`text-[10px] flex items-center gap-1 min-w-0 ${
                          occupato ? "text-fg-muted" : "text-accent"
                        }`}
                      >
                        {occupato && (
                          <PlayerAvatar nome={occupato} size={18} className="shrink-0" />
                        )}
                        <span className="truncate">
                          {occupato ? tr("↔ {{v1}}", { v1: occupato }) : "libero"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Panchina: nessun vincolo di ruolo sui panchinari. */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-fg-muted mb-1">
              {tr("Panchina")}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {bench.map((b, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onSelect("bench", idx)}
                  className="text-left px-2 py-1.5 rounded border border-info/40 bg-primary-deep/40 hover:border-info hover:bg-info/10 transition"
                >
                  <div className="font-mono font-bold text-xs text-fg">P{idx + 1}</div>
                  <div
                    className={`text-[10px] flex items-center gap-1 min-w-0 ${
                      b.nome ? "text-fg-muted" : "text-accent"
                    }`}
                  >
                    {b.nome && <PlayerAvatar nome={b.nome} size={18} className="shrink-0" />}
                    <span className="truncate">
                      {b.nome ? tr("↔ {{v1}}", { v1: b.nome }) : "libera"}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-2 border-t border-primary-soft flex items-center gap-2">
          <div className="text-[10px] text-fg-muted flex-1">
            <span className="text-success">✓</span> compatibile ·{" "}
            <span className="text-warning">⚠</span> fuori ruolo (malus -1) ·{" "}
            <span className="text-danger">✗</span> {tr("non schierabile")}
          </div>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs text-fg-muted hover:text-fg shrink-0"
          >
            {tr("Annulla")}
          </button>
        </div>
      </div>
    </div>
  );
}
